import { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { APIEndpoint, DFPAPIEndpoint } from '../../../utils/axios';
import { DataGrid, EditModal, NGODatePicker } from '../../../common';
import { errorFormatter, getCultureFromLs } from '../../../utils/commonFunctions';
import { customSelSearch } from '../../../utils/configs';
import LoadingGIF from '../../../assets/loadingIn.gif';
import moment from 'moment';
import CustomSelect from '../../../common/CustomSelect.jsx';
import { ThemeContext } from '../../../contexts/themeContext';
import { useTranslation } from 'react-i18next';
import cartIcon from '../../../assets/icons/carts.svg';
import productsIcon from '../../../assets/products.svg';
import employeesIcon from '../../../assets/employees.svg';
import searchIcon from '../../../assets/icons/search.svg';
import { RosterDetailsView } from './RosterDetailsView';
import { NotFoundError } from '../../../components/Error_Page/NotFoundError';

import usePermissions from '../../../utils/userPermissions';

export const RosterPage = () => {
  const { t } = useTranslation();
  const { themedColor } = useContext(ThemeContext);

  const { hasPermission } = usePermissions('view', 'orn:Account:ViewCrewRoster');

  const [errorMessage, setErrorMessage] = useState('');
  const [crewMembers, setCrewMembers] = useState([]);
  const [countryDetails, setCountryDetails] = useState({});
  const [selectedCrewId, setSelectedCrewId] = useState(null);
  const [crewName, setCrewName] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchKey, setSearchKey] = useState('');

  const [rosterData, setRosterData] = useState([]);
  const [activeCrewModalItem, setActiveCrewModalItem] = useState(null);
  const [modalTab, setModalTab] = useState('orders');
  const [modalTitle, setModalTitle] = useState('Flight Orders & Receipts');
  const [orderDetails, setOrderDetails] = useState({ orders: [], itemsSold: [] });
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const tableRef = useRef(null);
  const [resetGrid, setResetGrid] = useState(false);

  const onSearch = (e) => {
    setSearchKey(e.target.value);
  };

  const sType = localStorage.getItem('sType');
  const DOMAIN = (sType === 'dfp' ? DFPAPIEndpoint : APIEndpoint);

  const STATUS_CONFIG = {
    'Completed': { label: 'Completed', bg: 'rgba(40,167,69,0.2)', color: '#28a745', border: '#28a745' },
    'Arrived': { label: 'Arrived', bg: 'rgba(40,167,69,0.15)', color: '#28a745', border: '#28a745' },
    'Departed': { label: 'Departed', bg: 'rgba(23,162,184,0.2)', color: '#17a2b8', border: '#17a2b8' },
    'Scheduled': { label: 'Scheduled', bg: 'rgba(108,117,125,0.2)', color: '#adb5bd', border: '#adb5bd' },
    'Delayed': { label: 'Delayed', bg: 'rgba(255,193,7,0.2)', color: '#ffc107', border: '#ffc107' },
    'SignificantlyDelayed': { label: 'Sig. Delayed', bg: 'rgba(253,126,20,0.2)', color: '#fd7e14', border: '#fd7e14' },
    'Rescheduled': { label: 'Rescheduled', bg: 'rgba(111,66,193,0.2)', color: '#9b59b6', border: '#9b59b6' },
    'Cancelled': { label: 'Cancelled', bg: 'rgba(220,53,69,0.2)', color: '#dc3545', border: '#dc3545' },
    'Diverted': { label: 'Diverted', bg: 'rgba(220,53,69,0.15)', color: '#e74c3c', border: '#e74c3c' },
    'ReturnedToBase': { label: 'Returned to Base', bg: 'rgba(220,53,69,0.15)', color: '#e74c3c', border: '#e74c3c' },
    'Early': { label: 'Early', bg: 'rgba(32,178,170,0.2)', color: '#20b2aa', border: '#20b2aa' },
  };

  const StatusCellRenderer = (params) => {
    if (!params.data) return null;
    const cfg = STATUS_CONFIG[params.value] || { label: params.value || '-', bg: 'rgba(108,117,125,0.2)', color: '#adb5bd', border: '#adb5bd' };
    return (
      <span style={{
        padding: '3px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
        backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`
      }}>{cfg.label}</span>
    );
  };

  const RosterActionCell = (params) => {
    const data = params?.data;
    if (!data) return null;

    // Only Completed flights have reconciled sales session orders/products
    const isCompleted = data.status === 'Completed' || data.isCompleted;

    const handleActionClick = (e, actionValue) => {
      let actionLabel = 'Flight Orders & Receipts';
      if (actionValue === 'viewOrders') { setModalTab('orders'); actionLabel = 'Flight Orders & Receipts'; }
      else if (actionValue === 'viewProducts') { setModalTab('items'); actionLabel = 'Products Sold'; }
      else if (actionValue === 'viewCrew') { setModalTab('crew'); actionLabel = 'Cabin Crew Roster'; }

      const title = `${actionLabel} - ${data.flightNumber} (${data.origin} ➔ ${data.destination})`;
      setModalTitle(title);
      setActiveCrewModalItem(data);
    };

    return (
      <div className="flexRow">
        {isCompleted && (
          <>
            <div className="margRight10">
              <button onClick={(e) => handleActionClick(e, 'viewOrders')} title="View Orders & Receipts" className="margRight10">
                <img className="edit" alt="Orders" src={cartIcon} style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            <div className="margRight10">
              <button onClick={(e) => handleActionClick(e, 'viewProducts')} title="View Products Sold" className="margRight10">
                <img className="edit" alt="Products" src={productsIcon} style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
          </>
        )}
        <div className="margRight10">
          <button onClick={(e) => handleActionClick(e, 'viewCrew')} title="View Cabin Crew Roster">
            <img className="edit" alt="Crew" src={employeesIcon} style={{ width: '20px', height: '20px' }} />
          </button>
        </div>
      </div>
    );
  };

  const Columns = [
    { field: 'flightDateStr', headerName: 'Flight Date', flex: 1, minWidth: 130 },
    { field: 'flightNumber', headerName: 'Flight No', flex: 1, minWidth: 110 },
    { field: 'routeStr', headerName: 'Route', flex: 1, minWidth: 120 },
    { field: 'timingsStr', headerName: 'Departure - Arrival', flex: 1, minWidth: 170 },
    { field: 'aircraftNumber', headerName: 'Tail No', flex: 1, minWidth: 110 },
    { field: 'status', headerName: 'Status', flex: 1, minWidth: 120, cellRenderer: StatusCellRenderer },
    { field: 'crewCount', headerName: 'Crew Count', flex: 1, minWidth: 110 },
    { field: 'crewShareStr', headerName: 'Crew Share', flex: 1, minWidth: 120 },
    { field: 'actions', headerName: 'Actions', cellRenderer: RosterActionCell, pinned: 'right', minWidth: 150 }
  ];


  useEffect(() => {
    const el = document.querySelector('.content-container');
    if (!el) return;
    if (activeCrewModalItem) {
      el.classList.add('modal-overlay-bg2');
    } else {
      el.classList.remove('modal-overlay-bg2');
    }
    return () => {
      el.classList.remove('modal-overlay-bg2');
    };
  }, [activeCrewModalItem]);

  const handleCloseModal = () => {
    setActiveCrewModalItem(null);
    setModalTab('orders');
    setOrderDetails({ orders: [], itemsSold: [] });
  };

  const formattedRosterRows = useMemo(() => {
    return (rosterData || []).map(item => {
      const currency = countryDetails?.currency?.symbol || '₹';
      const depStr = item.departureDateTime ? moment(item.departureDateTime).format('HH:mm') : '-';
      const arrStr = item.arrivalDateTime ? moment(item.arrivalDateTime).format('HH:mm') : '-';
      const rescheduledIndicator = item.status === 'Rescheduled' ? ' ✦' : '';
      return {
        ...item,
        flightDateStr: (item.departureDateTime || item.flightDate)
          ? moment(item.departureDateTime || item.flightDate).format('DD MMM YYYY') : '-',
        routeStr: `${item.origin || ''} ➔ ${item.destination || ''}`,
        timingsStr: `${depStr} - ${arrStr}${rescheduledIndicator}`,
        bobSalesStr: `${currency} ${item.salesSummary?.totalBoBSales || 0}`,
        cashSalesStr: `${currency} ${item.salesSummary?.totalCashSales || 0}`,
        cardSalesStr: `${currency} ${item.salesSummary?.totalCardSales || 0}`,
        crewCount: item.salesSummary?.crewCount || (item.cabinCrew || []).length || 0,
        crewShareStr: `${currency} ${item.crewShare ? item.crewShare.toFixed(2) : '0.00'}`
      };
    });
  }, [rosterData, countryDetails]);

  const minAllowedDate = useMemo(() => moment().subtract(3, 'months').startOf('day').toDate(), []);
  const maxAllowedDate = useMemo(() => moment().add(10, 'days').endOf('day').toDate(), []);

  const fetchRosterRows = async (page = 1, limit = 50, search = '') => {
    const start = (page - 1) * limit;
    const end = start + limit;
    const filteredRows = formattedRosterRows.filter(row => {
      if (search) {
        const s = search.toLowerCase();
        return (row.flightNumber || '').toLowerCase().includes(s) ||
          (row.origin || '').toLowerCase().includes(s) ||
          (row.destination || '').toLowerCase().includes(s);
      }
      return true;
    });
    return { items: filteredRows.slice(start, end), count: filteredRows.length };
  };

  useEffect(() => {
    setResetGrid(prev => !prev);
  }, [formattedRosterRows]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    getCrewMembers();
  }, []);

  useEffect(() => {
    if (!startDate || !endDate || !crewName.length || !crewName[0]?.value) {
      setRosterData([]);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchRosterData();
  }, [startDate, endDate, crewName]);

  const getCrewMembers = async () => {
    try {
      const resp2 = await DOMAIN.get('sandboxing/employees?includeAdmin=no');
      if (resp2.result.length > 0) {
        const options = [{ key: 0, label: 'Select Crew', value: "" }];
        resp2.result.forEach((fieldValue, index) => {
          options.push({
            key: fieldValue.employeeId,
            label: `${fieldValue.employeeName} (${fieldValue.employeeCode}) ${fieldValue.employeeId} ${index}`,
            value: `${fieldValue.employeeCode}`,
            employeeId: fieldValue.employeeId
          });
        });
        const uniqueMembers = Array.from(
          new Map(options.map(item => [`${item.key}_${item.value}`, item])).values()
        );
        setCrewMembers(uniqueMembers);
      }
    } catch (error) {
      console.error('Error fetching crew members:', error);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      let err = null;
      try {
        err = JSON.parse(error?.message || '{}');
      } catch (e) {
        // Fallback if error.message is not valid JSON
      }
      if (err?.data && err.data?.errors) {
        let errorMessageFormatted = errorFormatter(err.data.errors[0]?.message);
        setErrorMessage(errorMessageFormatted || 'Error in fetching crew members!');
      } else {
        setErrorMessage('Error in fetching crew members!');
      }
      errorTimerRef.current = setTimeout(() => setErrorMessage(''), 4000);
    }


  }

  const fetchRosterData = async () => {
    setIsDownloading(true);
    const targetEmpId = parseInt(selectedCrewId, 10);
    const body = {};
    if (startDate) {
      body.startDate = moment(startDate).format();
    }
    if (endDate) {
      body.endDate = moment(endDate).format();
    }
    if (!isNaN(targetEmpId) && targetEmpId !== 0) {
      body.employeeId = targetEmpId;
    }
    try {
      const resp = await DOMAIN.post('sales/getCrewFlightRoster', body);
      setCountryDetails(getCultureFromLs());
      if (resp && Array.isArray(resp.sectorWiseSummary)) {
        setRosterData(resp.sectorWiseSummary);
      } else {
        setRosterData([]);
      }
      setIsDownloading(false);
    } catch (error) {
      setIsDownloading(false);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      let err = null;
      try {
        err = JSON.parse(error?.message || '{}');
      } catch (e) {
        // Fallback if error.message is not valid JSON
      }
      if (err?.data && err.data?.errors) {
        let errorMessageFormatted = errorFormatter(err.data.errors[0]?.message);
        setErrorMessage(errorMessageFormatted || 'Error in fetching data!');
      } else {
        setErrorMessage('Error in fetching data!');
      }
      errorTimerRef.current = setTimeout(() => setErrorMessage(''), 4000);
    }
  };

  const fetchFlightOrders = async (sessionId) => {
    setLoadingOrderDetails(true);
    try {
      const body = { sessionId };
      const targetEmpId = parseInt(selectedCrewId, 10);
      if (!isNaN(targetEmpId) && targetEmpId !== 0) {
        body.employeeId = targetEmpId;
      }
      const resp = await DOMAIN.post('sales/getFlightOrders', body);
      if (resp && resp.orders) {
        setOrderDetails(prev => ({
          ...prev,
          sessionId,
          employee: resp.employee,
          employeeSalesSummary: resp.employeeSalesSummary,
          orders: resp.orders
        }));
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const fetchFlightItemsSold = async (sessionId) => {
    setLoadingOrderDetails(true);
    try {
      const body = { sessionId };
      const targetEmpId = parseInt(selectedCrewId, 10);
      if (!isNaN(targetEmpId) && targetEmpId !== 0) {
        body.employeeId = targetEmpId;
      }
      const resp = await DOMAIN.post('sales/getFlightItemsSold', body);
      if (resp && resp.itemsSold) {
        setOrderDetails(prev => ({
          ...prev,
          sessionId,
          employee: resp.employee || prev.employee,
          itemsSold: resp.itemsSold
        }));
      }
    } catch (err) {
      console.error('Error fetching items sold:', err);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  useEffect(() => {
    if (!activeCrewModalItem || !activeCrewModalItem.id || modalTab === 'crew') {
      if (modalTab !== 'crew') {
        setOrderDetails({ sessionId: null, orders: [], itemsSold: [], employeeSalesSummary: {} });
      }
      return;
    }

    // Only fetch orders/items for completed flights that have a reconciled sales session
    const isActionableStatus = activeCrewModalItem.status === 'Completed';
    if (!isActionableStatus) {
      setOrderDetails({ sessionId: null, orders: [], itemsSold: [], employeeSalesSummary: {} });
      return;
    }

    const sessionId = activeCrewModalItem.id;

    if (modalTab === 'orders') {
      if (orderDetails.sessionId !== sessionId || !orderDetails.orders || orderDetails.orders.length === 0) {
        fetchFlightOrders(sessionId);
      }
    } else if (modalTab === 'items') {
      if (orderDetails.sessionId !== sessionId || !orderDetails.itemsSold || orderDetails.itemsSold.length === 0) {
        fetchFlightItemsSold(sessionId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrewModalItem, modalTab]);

  const onCrewFilterChange = (e) => {
    setCrewName([e]);
    setSelectedCrewId(e?.employeeId);
  };



  const errorTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const handleStartDateChange = (e) => {
    const val = e?.target?.value || null;
    setStartDate(val);
    if (val && endDate && moment(val).isAfter(moment(endDate))) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (e) => {
    const val = e?.target?.value || null;
    setEndDate(val);
  };


  if (!hasPermission) {
    return <NotFoundError />;
  }

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
      <>
        <div className='flexRow width100 margBot10 margTop8 justifyContentSpaceBetween alignItemsCenter'>
          {/* LEFT FILTERS */}
          <div className="flexRow justifyContentFlexStart alignItemsCenter">
            <div className="saleType-div">
              <CustomSelect
                onChange={onCrewFilterChange}
                options={crewMembers}
                placeHolder={"Search Crew"}
                width={"17rem"}
                styles={customSelSearch(themedColor)}
                value={crewMembers?.filter(function (option) {
                  return (
                    crewName.length && crewName[0] &&
                    crewName.find((sc) => sc.label === option.label)
                  );
                })}
              />
            </div>
          </div>

          {/* CENTER SEARCH BAR (matches estimation/crew-roaster pattern) */}
          <div className="flexRow justifyContentCenter alignItemsCenter">
            <div className="margBot10 underline">
              <span><img className="icon-size" alt={t('filters.searchText')} src={searchIcon} /></span>
              <span><input className="search" onChange={onSearch} type="search" placeholder={t('filters.searchText')} value={searchKey} /></span>
            </div>
          </div>

          {/* RIGHT FILTERS */}
          <div className="flexRow justifyContentFlexEnd alignItemsCenter" style={{ gap: '15px' }}>
            {isDownloading ? (
              <button className="margRight" title={t('filters.downloadingMaster')}>
                <img src={LoadingGIF} className="margBottom0 icon-size-min" alt="Loading" />
              </button>
            ) : null}

            <div style={{ display: 'flex', gap: '10px' }}>
              <NGODatePicker
                style={{ width: '12rem' }}
                value={startDate}
                name="startDate"
                minDate={minAllowedDate}
                maxDate={endDate ? new Date(endDate) : maxAllowedDate}
                isclearable={true}
                placeholder={t('Start Date')}
                placeholdertext={t('datePickerPlaceholder')}
                onChange={handleStartDateChange}
              />
              <NGODatePicker
                style={{ width: '12rem' }}
                value={endDate}
                name="endDate"
                minDate={startDate ? new Date(startDate) : minAllowedDate}
                maxDate={maxAllowedDate}
                disabled={!startDate}
                isclearable={true}
                placeholder={t('End Date')}
                placeholdertext={t('datePickerPlaceholder')}
                onChange={handleEndDateChange}
              />
            </div>
          </div>
        </div>

        {(errorMessage.length) ?
          <>
            <div style={{ display: 'flex', width: '100%', margin: '1rem', marginLeft: '0rem', padding: '0.3rem' }} className={"confirmation_popup alert_error"}>
              {errorMessage[0] + errorMessage[1].toUpperCase() + errorMessage.slice(2)}
            </div>
          </> : null}

        <div className='flexCol full-flex ag-theme-alpine-dark' >
          <DataGrid
            ref={tableRef}
            className="full-flex"
            columns={[...Columns]}
            showDefaultFilters={false}
            searchKey={searchKey}
            getRows={fetchRosterRows}
            initialPageLimit={50}
            resetDataSource={resetGrid}
            noResultsMessage={"No Flight Data Available"}
            columnResize={true}
          />
        </div>

        {activeCrewModalItem && (
          <EditModal
            title={modalTitle}
            onClose={handleCloseModal}
            modalFormStatusObj={{ text: '' }}
            modalCustomButtons={[
              {
                label: 'Close',
                attribute: 'button',
                type: 'button',
                disabled: false,
                className: 'close-button',
                action: () => {
                  handleCloseModal();
                  return false;
                }
              }
            ]}
            showFieldsInColumn={true}
          >
            <RosterDetailsView
              modalTab={modalTab}
              activeCrewModalItem={activeCrewModalItem}
              orderDetails={orderDetails}
              loadingOrderDetails={loadingOrderDetails}
              countryDetails={countryDetails}
              crewName={crewName}
            />
          </EditModal>
        )}
      </>
    </div>
  );
};